import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormState } from 'react-final-form';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';
import { DEFAULT_CURRENCY } from '../../../../../../utils/currency';
import { RG, SD } from '../../../../../../utils/constants';
import unexpectedErrorMessage from '../../../../../../utils/unexpectedErrorMessage';
import { getStripePublishableKey } from '../../../../../../utils/payments';
import Button from '../../../../../01_atoms/Button';
import FundraisingCosts from '../../../../../02_molecules/FundraisingCosts';
import RevolutStripeForm, { completePayment } from '../../../../../02_molecules/RevolutStripeForm';
import TotalCost from '../../../../../02_molecules/TotalCost';

import styles from './index.module.scss';

const stripePromise = loadStripe(getStripePublishableKey());

const RevolutForm = ({
  orderId = null,
  onSubmitForm,
  onError,
  buttonType = 'primary',
  showFundraisingCosts = false,
  fundraisingCostsTooltip = '',
}) => {
  const { values } = useFormState();
  const [visible, setVisible] = useState(false);
  const pageUrl = `${values.page_url}?type=${values.donation_type}`;
  const giftaid = values.gift_aid ?? false;

  // Hide Revolut element if amount less than 1. Revolut doesn't allow to
  // set <0.3GBP and <0.5EUR.
  useEffect(() => {
    const amount = +values.amount[values.donation_type];
    setVisible(amount >= 1);
  }, [values.amount[values.donation_type]]);

  const amount =
    values.fundraisingCostsApplied && values.donation_type === 'single'
      ? values.totalAmount
      : +values.amount[values.donation_type];

  // Unfortunately we can't use common Elements wrapper from Payments component
  // for Revolut, since necessary values aren't passed at the moment of calling.
  return (
    <div className={styles['revolut-form']} data-component="revolut-form">
      <div className={styles['revolut-form__container']}>
        <div className={styles['revolut-form__container-left']}>
          <div className={styles['revolut-form__title']}>Revolut Pay</div>
          {visible && (
            <Elements
              stripe={stripePromise}
              // Math.round(amount * 100) is important here, since sometimes JS produces
              // odd results for multiplying, for example 2086.07 * 100 = 208607.00000000003.
              options={{
                mode: values.donation_type === 'monthly' ? 'setup' : 'payment',
                currency: DEFAULT_CURRENCY.toLowerCase(),
                paymentMethodTypes: ['revolut_pay'],
                ...(values.donation_type === 'monthly' ? {} : { amount: Math.round(amount * 100) }),
              }}
            >
              <RevolutStripeForm
                orderId={orderId}
                onCompletePayment={async (stripe, elements) => {
                  const result = await completePayment(
                    stripe,
                    elements,
                    orderId,
                    values['donation_type'] === 'monthly' ? RG : SD,
                    pageUrl,
                    giftaid,
                    values.amount[values.donation_type],
                  );
                  if (result.type === 'error') {
                    onError({
                      message: 'Sorry, we could not submit your donation.',
                      subline: unexpectedErrorMessage(),
                      additionalMessage: result.errorMessage,
                      paymentMethodId: values.payment_method_id,
                    });
                  }
                }}
              />
            </Elements>
          )}

          {showFundraisingCosts &&
            values.donation_type === 'single' &&
            values.amount.single > 0 && (
              // If you are changing the condition ^, make sure it is
              // consistent with isFundraisingCostsVisible() function.
              <>
                <div className={styles['revolut-form__description']}>
                  Do you want to cover the costs of this transaction?
                </div>
                <FundraisingCosts tooltipText={fundraisingCostsTooltip} />
              </>
            )}

          <TotalCost values={values} />

          <Button
            data-component="inline-donation-form-revolut-submit"
            type={buttonType}
            isBlock
            className={styles['revolut-form__submit-button']}
            onClick={onSubmitForm}
          >
            Pay with Revolut
          </Button>
        </div>
      </div>
    </div>
  );
};

RevolutForm.propTypes = {
  orderId: PropTypes.number,
  onSubmitForm: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  buttonType: PropTypes.oneOf(['primary', 'emergency']),
  showFundraisingCosts: PropTypes.bool,
  fundraisingCostsTooltip: PropTypes.string,
};

export default RevolutForm;
